<script lang="ts" setup>
import Button from 'primevue/button'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import TableFilter from '@/components/table/TableFilter.vue'
import TableSearchInput from '@/components/table/TableSearchInput.vue'
import PageHeader from '@/components/global/page-header/PageHeader.vue'
import { onMounted, ref, computed } from 'vue'
import { Icon } from '@iconify/vue'
import { RouterLink, useRouter } from 'vue-router'
import { getCustomerList } from '@/api/customers'
import { getProductList } from '@/api/products'
import { getPackingList } from '@/api/packingList'
import { formatedDate } from '@/constants'
import { getOrders } from '@/api/orders'
import { getBatchList } from '@/api/batches'
import type { Customer } from '@/types/customer-types'
import type { Supplier } from '@/types/supplier-types'
import { getSupplierList } from '@/api/suppliers'
import { type PackingList, PackingListState } from '@/types/packingList-types'
import StatusTag from '@/components/table/StatusTag.vue'

const customerList = ref<Customer[]>([])
const router = useRouter()
const packingList = ref<PackingList[]>([])
const loading = ref<boolean>(true)
const searchQuery = ref('')
const productsList = ref<any[]>([])
const orderList = ref<any[]>([])
const batches = ref<any[]>([])
const supplierList = ref<Supplier[]>([])
const allFilters = ref<string[]>([])

const combinedFilterOptions = [
  { value: 'all', label: 'All', id: 'combined-all' },
  { value: 'pending', label: 'Pending', id: 'combined-pending' },
  { value: 'dispatched', label: 'Dispatched', id: 'combined-dispatched' },
  { value: 'delivered', label: 'Delivered', id: 'combined-delivered' },
  { value: 'supplier', label: 'Supplier Packing List', id: 'combined-supplier' },
  { value: 'customer', label: 'Customer Packing List', id: 'combined-customer' }
]

const getStatusText = (status: PackingListState) => {
  switch (Number(status)) {
    case PackingListState.Dispatched:
      return 'Dispatched'
    case PackingListState.Delivered:
      return 'Delivered'
    case PackingListState.Pending:
    default:
      return 'Pending'
  }
}

const getStatusColor = (status: PackingListState) => {
  switch (Number(status)) {
    case PackingListState.Dispatched:
      return 'warning'
    case PackingListState.Delivered:
      return 'success'
    case PackingListState.Pending:
    default:
      return 'info'
  }
}

const getStatusValue = (status: PackingListState) => {
  switch (Number(status)) {
    case PackingListState.Dispatched:
      return 'dispatched'
    case PackingListState.Delivered:
      return 'delivered'
    case PackingListState.Pending:
    default:
      return 'pending'
  }
}

const getOrder = (id: string) => {
  return orderList.value.find((order: { id: string }) => order.id === id)?.orderNumber
}

const filteredOrderList = computed(() => {
  const query = searchQuery.value.toLowerCase()
  let filtered = packingList.value.filter((packingList: { packingListId?: string }) =>
    (packingList.packingListId ?? '').toLowerCase().includes(query)
  )

  if (allFilters.value.length > 0 && !allFilters.value.includes('all')) {
    filtered = filtered.filter((packing: any) => {
      const packingStatus = getStatusValue(packing.packingListState)
      const packingType = packing.packingListType === 0 ? 'supplier' : 'customer'
      return allFilters.value.includes(packingStatus) || allFilters.value.includes(packingType)
    })
  }

  return filtered
})

const getOrderOrBatchName = (id: string | null, type: 'order' | 'batch') => {
  if (!id) {
    return type === 'order' ? 'Order Not Found' : 'Batch Not Found'
  }

  if (type === 'order') {
    const order = orderList.value.find((order: any) => order.id === id)
    return order ? order.orderNumber : 'Order Not Found'
  }

  if (type === 'batch') {
    const batch = batches.value.find((batch: any) => batch.id === id)
    return batch ? batch.purchaseOrderReference : 'Batch Not Found'
  }

  return 'Invalid Type'
}

const getCustomerOrSupplierName = (packingList: any) => {
  if (packingList.packingListType === 0) {
    const supplier = supplierList.value.find(s => s.id === packingList.finishingSupplierId)
    return supplier?.name || 'Supplier Not Found'
  } else {
    const customer = customerList.value.find(c => c.id === packingList.customerId)
    return customer?.businessName || 'Customer Not Found'
  }
}

const handleFilterChange = (filters: string[]) => {
  allFilters.value = filters
}

onMounted(async () => {
  packingList.value = await getPackingList()
  orderList.value = await getOrders()
  customerList.value = await getCustomerList()
  productsList.value = await getProductList()
  batches.value = await getBatchList()
  supplierList.value = await getSupplierList()
  loading.value = false
})
</script>

<template>
  <PageHeader
    title="Packing List"
    :description="`Total: ${packingList.length} | Filtered: ${filteredOrderList.length}`"
    buttonText="Add Order"
    buttonIcon="mdi:add-to-photos"
    :buttonClick="() => router.push('/orders/add')"
  />
  <DataTable
    :value="filteredOrderList"
    paginator
    :rows="10"
    tableStyle="min-width: 50rem"
    stripedRows
    paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
    :rowsPerPageOptions="[10, 25]"
    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Packing Lists"
    :loading="loading"
    :pt="{
      table: loading && 'min-h-[30rem]'
    }"
    :pt-options="{
      mergeProps: true
    }"
    class="w-full"
  >
    <template #empty> <p class="text-center">No packing lists found.</p> </template>

    <template #header>
      <div class="flex items-center justify-between font-normal gap-4">
        <TableSearchInput v-model:searchQuery="searchQuery" />
        <TableFilter
          :filter-options="combinedFilterOptions"
          filter-title="Filter Packing List Status and Type"
          @filter-change="handleFilterChange"
        />
      </div>
    </template>

    <Column field="packingListId" header="Packing List ID">
      <template #body="slotProps">
        <RouterLink
          :to="`/packingList/${slotProps.data.id}`"
          class="font-medium text-neutral-800 block hover:underline"
        >
          {{ slotProps.data.packingListId }}
        </RouterLink>
      </template>
    </Column>
    <Column field="packingListType" header="Packing List Type">
      <template #body="slotProps">
        {{ slotProps.data.packingListType === 0 ? 'Supplier Packing List' : 'Customer Packing List' }}
      </template>
    </Column>

    <Column field="customerSupplier" header="Customer/Supplier Name">
      <template #body="slotProps">
        <RouterLink
          v-if="slotProps.data.packingListType === 0"
          :to="`/suppliers/${slotProps.data.finishingSupplierId}`"
          class="font-medium text-neutral-800 block hover:underline"
        >
          {{ getCustomerOrSupplierName(slotProps.data) }}
        </RouterLink>
        <RouterLink
          v-else
          :to="`/customers/${slotProps.data.customerId}`"
          class="font-medium text-neutral-800 block hover:underline"
        >
          {{ getCustomerOrSupplierName(slotProps.data) }}
        </RouterLink>
      </template>
    </Column>

    <Column field="packingListDate" header="Packing List Date">
      <template #body="slotProps">
        {{ formatedDate(slotProps.data.packingListDate) }}
      </template>
    </Column>

    <Column field="orderNumber" header="Order Number">
      <template #body="slotProps">
        <span>{{ getOrderOrBatchName(slotProps.data.orderId || slotProps.data.batchId, slotProps.data.orderId ? 'order' : 'batch') }}</span>
      </template>
    </Column>

    <Column field="packingListState" header="Status">
      <template #body="slotProps">
        <StatusTag
          :status="getStatusText(slotProps.data.packingListState)"
          :color="getStatusColor(slotProps.data.packingListState)"
        />
      </template>
    </Column>

    <Column field="action">
      <template #body="slotProps">
        <RouterLink
          :to="`/packingList/${slotProps.data.id}`"
          class="flex items-center gap-x-2 group hover:text-primary-default"
        >
          <span>More Details</span>
          <Icon
            icon="material-symbols:more-up"
            class="text-2xl group-hover:translate-x-2 transition-transform text-primary-500"
          />
        </RouterLink>
      </template>
    </Column>

    <template #paginatorprevpagelinkicon>
      <Button size="small" class="space-x-1 items-center" outlined severity="secondary">
        <Icon icon="material-symbols:chevron-left" class="text-2xl" />
        <span>Previous</span>
      </Button>
    </template>
    <template #paginatornextpagelinkicon>
      <Button size="small" outlined severity="secondary" class="space-x-1 items-center">
        <span>Next</span>
        <Icon icon="material-symbols:chevron-right" class="text-2xl" />
      </Button>
    </template>
  </DataTable>
</template>